import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Visibility } from '../domain/collections';
import { useRepositories } from '../hooks/useRepositories';
import { getApiErrorMessage } from '../utils/errors';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Globe, Lock } from 'lucide-react';

const VISIBILITY_CHOICES: {
  value: Visibility;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'open',
    title: 'Open',
    description: 'Everyone you’re connected with sees it.',
    icon: <Globe className="size-5" />,
  },
  {
    value: 'private',
    title: 'Private',
    description: 'Only people you invite to it. It can’t be made open later.',
    icon: <Lock className="size-5" />,
  },
];

export default function CreateCollection() {
  const navigate = useNavigate();
  const { collections } = useRepositories();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('open');
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the collection a name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await collections.create({
        name: name.trim(),
        description: description.trim(),
        visibility,
        commentsEnabled,
      });
      navigate(`/collections/${created.id}`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create the collection.'));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-8 text-4xl font-bold tracking-tight text-foreground">New collection</h1>

      <form onSubmit={submit} className="space-y-8 rounded-3xl bg-card p-6 md:p-8">
        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={100}
            placeholder="e.g. Garage Sale"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="description">Description (optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What goes in it, and who it's for"
            className="mt-1"
          />
        </div>

        <fieldset>
          <legend className="mb-3 text-sm font-medium text-foreground">Who can see it</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {VISIBILITY_CHOICES.map((choice) => (
              <div
                key={choice.value}
                className={`flex gap-3 rounded-2xl p-4 transition-colors ${
                  visibility === choice.value
                    ? 'bg-secondary text-secondary-foreground'
                    : 'bg-muted hover:bg-secondary/60'
                }`}
              >
                <input
                  type="radio"
                  id={`visibility-${choice.value}`}
                  name="visibility"
                  value={choice.value}
                  checked={visibility === choice.value}
                  onChange={() => setVisibility(choice.value)}
                  className="mt-1 accent-primary"
                />
                <label htmlFor={`visibility-${choice.value}`} className="flex-1 cursor-pointer">
                  <span className="flex items-center gap-2 font-semibold text-foreground">
                    {choice.icon}
                    {choice.title}
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {choice.description}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </fieldset>

        <div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="comments_enabled"
              checked={commentsEnabled}
              onCheckedChange={(checked) => setCommentsEnabled(checked === true)}
            />
            <Label htmlFor="comments_enabled" className="cursor-pointer">
              Allow comments
            </Label>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            When off, existing comments are hidden but not deleted.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            className="rounded-full text-primary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-full" disabled={saving}>
            {saving ? 'Creating…' : 'Create collection'}
          </Button>
        </div>
      </form>
    </div>
  );
}
